import { ChangeDetectorRef, Directive, inject, Input, OnInit } from '@angular/core'
import { Message, MessageService } from 'primeng/api'

import { SnapshotContext, SnapshotFileKind, SnapshotFileStatus, SnapshotResponse, SnapshotTab, SnapshotView } from '@data/interfaces/verification-snapshot.interface'
import { ApiVerificationSnapshotService } from '@data/services/modules/verification-snapshot/api-verification-snapshot.service'
import { loadErrorUiState, sectionUiState, SnapshotUiState, viewUiState } from './snapshot-state/snapshot-state.component'
import { FILE_STATUS_NOTES } from '@shared/components/ciec-analysis-view/snapshot-labels.constants'
import { AuthService } from '@data/services/modules/auth/auth.service'

/**
 * El aviso de un PDF que no se pudo abrir. Solo se pide un PDF que la foto marca `copied`, así que un 404
 * `FILE_NOT_AVAILABLE` ahí quiere decir que la copia ya no está en nuestro almacenamiento: reintentar no lo arregla.
 *
 * ! La API manda ese mismo código, y el mismo «Intenta de nuevo en unos minutos», a un PDF que todavía se copia
 * ! (`snapshot-files.service.ts` · `stream`): su `message` no se muestra aquí porque prometería un reintento que no
 * ! sirve. Lo distingue el estado `copied` que ya trae la vista (F-158).
 */
export function downloadErrorToast(response: SnapshotResponse<unknown>): Message {
  const summary = 'No se pudo abrir el PDF'
  if (response.status === 404 && response.code === 'FILE_NOT_AVAILABLE') return { severity: 'error', summary, detail: 'El archivo no está disponible; avísale a Operaciones.' }
  return { severity: 'error', summary, detail: 'Intenta de nuevo; si sigue igual, avísale a Operaciones.' }
}

/** El PDF abierto en el previsualizador. */
export interface SnapshotPreview {
  kind: SnapshotFileKind
  blob: Blob
  fileName: string
}

/**
 * Los PDF de una foto: estado de copia, nota, si se ofrece el botón y el previsualizador. Lo usan las pestañas por
 * `SnapshotTabBase` y el bloque Análisis por composición: Análisis no es una pestaña perezosa y no hereda de la
 * base, pero su opinión 32-D se abre igual que Buró y BIL (F-158).
 */
export class SnapshotFiles {
  /** PDF que se está pidiendo para abrirlo: el botón queda en «Abriendo…» y un doble clic no repite la petición. */
  downloading: SnapshotFileKind | null = null
  /** PDF abierto en `app-file-preview-dialog`; `null` con el visor cerrado, así el blob no se queda en memoria. */
  preview: SnapshotPreview | null = null

  constructor(
    private readonly viewOf: () => Pick<SnapshotView, 'files' | 'snapshot'> | null,
    private readonly api: ApiVerificationSnapshotService,
    private readonly messageService: MessageService,
    private readonly cd: ChangeDetectorRef,
    /** `key` del `<p-toast>` propio de quien la usa; sin ella el aviso va sin `key`, como en Buró y Legal. */
    private readonly toastKey?: string,
  ) {}

  /** Estado de copia del PDF de la foto: `null` si la foto no tiene ese archivo. */
  status(kind: SnapshotFileKind): SnapshotFileStatus | null {
    return this.viewOf()?.files?.find((file) => file.kind === kind)?.status ?? null
  }

  /** Nota visible junto al PDF (`FILE_STATUS_NOTES`); `null` si se descarga o la foto no tiene ese archivo. */
  note(kind: SnapshotFileKind): string | null {
    const status = this.status(kind)
    return status ? (FILE_STATUS_NOTES[status] ?? null) : null
  }

  /** El botón PDF se pinta si la foto registró el archivo y no es definitivo que ya no existe en Syntage. */
  offers(kind: SnapshotFileKind): boolean {
    const status = this.status(kind)
    return status !== null && status !== 'unavailable'
  }

  /**
   * Trae el PDF por el proxy del backend y lo abre en el previsualizador; solo un fallo avisa. Pide el PDF de la
   * revisión que se está viendo: en un arrendamiento congelado es la anclada, no la de un anexo posterior.
   */
  open(kind: SnapshotFileKind): void {
    const snapshot = this.viewOf()?.snapshot
    if (!snapshot?.id || this.downloading || this.status(kind) !== 'copied') return
    this.downloading = kind
    this.api.fetchFile(snapshot.id, kind, snapshot.viewedRevision).subscribe((response) => {
      this.downloading = null
      if (response.error || !response.data) this.messageService.add(this.toastKey ? { ...downloadErrorToast(response), key: this.toastKey } : downloadErrorToast(response))
      else this.preview = { kind, ...response.data }
      this.cd.markForCheck()
    })
  }

  /** «Descargar» dentro del visor: guarda el mismo archivo que se está viendo, con su nombre. */
  savePreview(): void {
    if (this.preview) this.api.saveBlob(this.preview.blob, this.preview.fileName)
  }

  /** Al cerrar el visor se suelta el blob. */
  closePreview(): void {
    this.preview = null
    this.cd.markForCheck()
  }
}

/**
 * Lo común de las pestañas Buró y deuda / Legal y corporativo: pedir su vista al abrirse, traducir el
 * sobre a un estado de `snapshot-state`, reintentar y abrir el PDF de la foto en el previsualizador.
 *
 * Se abre al primer clic porque el panel vive en `<ng-template pTemplate="content">`: PrimeNG 16 no
 * emite un evento por panel (`docs/design/lease-detail-tabs.md` § Nota sobre carga de datos), así que
 * «la pestaña resuelve sus datos» es instanciarse y pedir en `ngOnInit`.
 *
 * `V` es la vista con el `data` de cada sección ya tipado (`BureauView`, `LegalView`).
 */
@Directive()
export abstract class SnapshotTabBase<V extends SnapshotView = SnapshotView> implements OnInit {
  @Input() context: SnapshotContext | null = null
  /** Fecha del análisis original; respaldo del aviso de foto reconstruida si el sobre no trae `originalVerifiedAt`. */
  @Input() analyzedAt?: string | null

  view: V | null = null
  /** Estado que sustituye a la pestaña entera; `null` cuando hay foto que pintar. */
  envelopeState: SnapshotUiState | null = 'loading'

  protected abstract readonly tab: SnapshotTab

  protected readonly api = inject(ApiVerificationSnapshotService)
  protected readonly messageService = inject(MessageService)
  protected readonly cd = inject(ChangeDetectorRef)
  protected readonly auth = inject(AuthService)
  //* Después de `api`, `messageService` y `cd`: los inicializadores corren en orden de declaración.
  readonly pdfs = new SnapshotFiles(() => this.view, this.api, this.messageService, this.cd)

  /** El estado de una sección para `app-snapshot-state`, o `null` si tiene datos que pintar. */
  readonly sectionState = sectionUiState

  ngOnInit(): void {
    this.load(false)
  }

  retry(): void {
    this.load(true)
  }

  /**
   * Se puede buscar en Syntage el reporte de Buró y BIL que faltó (anexo tardío): con `leases.update`, sin foto
   * anclada y fuera de un arrendamiento congelado, donde el backend respondería 409.
   */
  get canAnnex(): boolean {
    return !!this.view?.snapshot && !this.view.snapshot.pinned && !this.context?.frozen && this.auth.userHasAuthorization('leases', 'update')
  }

  /** PDF que se está abriendo (`SnapshotFiles`); las plantillas de Buró y Legal lo leen aquí. */
  get downloading(): SnapshotFileKind | null {
    return this.pdfs.downloading
  }

  fileStatus(kind: SnapshotFileKind): SnapshotFileStatus | null {
    return this.pdfs.status(kind)
  }

  fileNote(kind: SnapshotFileKind): string | null {
    return this.pdfs.note(kind)
  }

  offersFile(kind: SnapshotFileKind): boolean {
    return this.pdfs.offers(kind)
  }

  openFile(kind: SnapshotFileKind): void {
    this.pdfs.open(kind)
  }

  /**
   * Campos de un registro sin forma fija (Infonavit, poderes: vacíos en las tres muestras medidas),
   * para pintarlos como etiqueta y valor sin perder lo que traigan.
   */
  entries(record: Record<string, unknown>): Array<{ key: string; value: string }> {
    return Object.entries(record).map(([key, value]) => ({ key, value: value === null || value === undefined ? '—' : typeof value === 'object' ? JSON.stringify(value) : String(value) }))
  }

  /** Recalcula lo derivado cada vez que llega una vista con foto. */
  protected onView(): void {
    // Cada pestaña deriva aquí lo que pinta.
  }

  protected load(isRetry: boolean): void {
    if (!this.context) return
    this.envelopeState = 'loading'
    this.api.getView(this.context, this.tab).subscribe((response) => {
      if (response.error || !response.data) {
        this.view = null
        this.envelopeState = loadErrorUiState(response)
        if (isRetry) this.messageService.add({ severity: 'error', summary: 'No se pudo cargar la sección', detail: 'Revisa tu conexión y vuelve a intentarlo.' })
      } else {
        this.view = response.data as V
        this.envelopeState = viewUiState(response.data)
        if (!this.envelopeState) this.onView()
      }
      this.cd.markForCheck()
    })
  }
}
